import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonMenu,
    IonMenuToggle,
    IonNote,
    IonToggle,
    Platform,
    ToggleCustomEvent,
} from '@ionic/angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserRoles } from '../../../user/entities/user-roles';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { AppConfiguration } from '../../../config/entities';
import { BuildType } from '../../../config/entities/build-type';
import { ConfigService } from '../../../config/services/config.service';
import { Logger, LoggingService } from '../../../logging/logging.service';
import { RoutingSegment } from '../../entities/routing-segment';
import { BrowserNavigationService } from '../../services/browser-navigation/browser-navigation.service';
import { MenuService } from '../../services/menu.service';
import { SplashScreenStateService } from '../../services/splash-screen-state.service';
import { VersionService } from '../../../version/version.service';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItemsFilterPipe } from '../../../config/pipes/menu-items-filter.pipe';
import { NgClass } from '@angular/common';
import { CheckFeaturesPipe } from '../../../config/pipes/check-features.pipe';
import { Capacitor } from '@capacitor/core';
import { CopyClipboardDirective } from '../../directives/copy-to-clipboard.directive';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'lib-curafida-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonMenu,
        IonContent,
        IonMenuToggle,
        IonItem,
        IonLabel,
        TranslatePipe,
        MenuItemsFilterPipe,
        RouterLink,
        NgClass,
        IonNote,
        IonIcon,
        IonFooter,
        CheckFeaturesPipe,
        IonToggle,
        CopyClipboardDirective,
    ],
})
export class SideMenuComponent implements OnInit {
    readonly userPreferencesService = inject(UserPreferencesService);

    isDebuggingAttributeEnabled = false;
    appConfig: AppConfiguration;
    routerActionSubscription: Subscription;
    roles: UserRoles[] | string[] = [];
    iosPlatform = false;
    BuildType = BuildType;
    @Input()
    contentID: string;
    isUserAuthenticated: boolean;
    readonly menuService = inject(MenuService);
    readonly versionService = inject(VersionService);
    protected readonly Capacitor = Capacitor;
    private log: Logger;

    constructor(
        public readonly router: Router,
        public readonly configService: ConfigService,
        private readonly authService: CurafidaAuthService,
        protected readonly platform: Platform,
        private splashScreenStateService: SplashScreenStateService,
        private loggingService: LoggingService,
        private browserNavigation: BrowserNavigationService,
    ) {
        this.log = this.loggingService.getLogger(this.constructor.name);
    }

    async ngOnInit() {
        this.iosPlatform = this.platform.is('ios');
        this.appConfig = this.configService.frontendConfig();

        // TODO: prüfen und verbessern!
        await this.initSideMenu();
        this.routerActionSubscription = this.router.events.subscribe(async (evt) => {
            if (evt instanceof NavigationEnd) {
                await this.initSideMenu();
            }
        });
    }

    async initSideMenu() {
        this.isUserAuthenticated = this.authService.isAuthenticated();
        this.roles = this.authService.getSession()?.user?.roles || [];
        this.isDebuggingAttributeEnabled = this.authService.getDebuggingAttribute();
        await firstValueFrom(this.versionService.loadUDI());
    }

    async logout() {
        this.splashScreenStateService.start();
        await this.authService.logout();
    }

    async openPage(url: string) {
        if (url) {
            await this.router.navigate([url]);
        }
    }

    switchLogging(ev: ToggleCustomEvent) {
        this.log.info('Enabling in-app logs');
        this.log.info('Using log level', this.loggingService.getGlobalLogLevelStr());
        this.loggingService.publishSwitchLogViewState(ev.detail.checked);
    }

    // Check if at least one of the categories is in the URL, for menu highlighting
    menuItemActive(categories: RoutingSegment[]) {
        for (const category of categories) {
            if (this.router.url.includes(category)) {
                return true;
            }
        }
        return false;
    }

    async login() {
        // das ist relevant um die nicht angemeldet Journey richtig zu setzen. Die Weiterleitung auf die login Seite ist mit den loginGuard verwaltet.
        await this.router.navigate([RoutingSegment.LOGIN]);
    }

    async onMenuOpen() {
        await this.initSideMenu();
    }

    openUserManual() {
        let url = '';
        if (!this.authService.isAuthenticated()) {
            url = 'https://handbuch.ztm.de/curafida-ueberblick';
        } else if (this.authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
            url = 'https://handbuch.ztm.de/curafida/handbuch/patientenansicht';
        } else if (!this.authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
            url = 'https://handbuch.ztm.de/curafida/handbuch/betreueransicht';
        }
        this.browserNavigation.openTargetBlank(url);
    }

    userManualLabel() {
        let label = '';
        if (!this.authService.isAuthenticated()) {
            label = 'USER.MANUAL.DEFAULT';
        } else if (this.authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
            label = 'USER.MANUAL.PATIENT';
        } else if (!this.authService.loggedInUser()?.roles.includes(UserRoles.PATIENT)) {
            label = 'USER.MANUAL.NON_PATIENT';
        }
        return label;
    }
}
